// Shared Archive Pursuit modal used by both the Pipeline (Kanban row
// actions) and the Opportunity Intake (3-dot menu). Archiving frees the
// pursuit's slot but keeps the record searchable / reportable.
//
// Reasons mirror the existing pipeline modal:
//   WON | LOST | NO_BID | CANCELLED
//
// Endpoint: POST /api/pursuits/{id}/archive
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../ui/select';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Archive,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ARCHIVE_REASONS = [
  { value: 'WON', label: 'Won', icon: CheckCircle2, tone: 'text-emerald-500' },
  { value: 'LOST', label: 'Lost', icon: XCircle, tone: 'text-red-500' },
  { value: 'NO_BID', label: 'No Bid', icon: AlertTriangle, tone: 'text-amber-500' },
  { value: 'CANCELLED', label: 'Cancelled', icon: XCircle, tone: 'text-slate-500' },
];

export default function ArchivePursuitModal({
  isOpen,
  onClose,
  pursuit,
  onArchived,
}) {
  const { authToken } = useAuth();
  const [archiveReason, setArchiveReason] = useState('');
  const [archiveNotes, setArchiveNotes] = useState('');
  const [isArchiving, setIsArchiving] = useState(false);

  const handleClose = () => {
    if (isArchiving) return;
    setArchiveReason('');
    setArchiveNotes('');
    onClose();
  };

  const handleArchive = async () => {
    if (!pursuit || !archiveReason) return;
    setIsArchiving(true);
    try {
      const res = await fetch(`${API_URL}/api/pursuits/${pursuit.id}/archive`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          archive_reason: archiveReason,
          archive_notes: archiveNotes || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to archive pursuit');
      }
      const data = await res.json();
      toast.success('Pursuit archived', {
        description: `Slot freed · ${data.active_pursuits ?? ''}${
          data.total_capacity >= 0 ? `/${data.total_capacity}` : ''
        } active`,
      });
      setArchiveReason('');
      setArchiveNotes('');
      if (onArchived) onArchived(data);
      onClose();
    } catch (e) {
      toast.error(e.message || 'Failed to archive pursuit');
    } finally {
      setIsArchiving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md" data-testid="archive-pursuit-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Archive className="w-5 h-5 text-slate-600" />
            Archive Pursuit
          </DialogTitle>
          <DialogDescription>
            Archived pursuits don't count toward your active pursuit limit and
            stay searchable for reporting.
          </DialogDescription>
        </DialogHeader>

        {pursuit && (
          <div className="space-y-4 py-2">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="font-medium text-slate-900">{pursuit.opportunity_name}</p>
              <p className="text-sm text-slate-500">
                {pursuit.solicitation_number || pursuit.agency_code || 'N/A'}
              </p>
            </div>

            <div className="space-y-2">
              <Label>
                Archive Reason <span className="text-red-500">*</span>
              </Label>
              <Select value={archiveReason} onValueChange={setArchiveReason}>
                <SelectTrigger data-testid="archive-reason-select">
                  <SelectValue placeholder="Select reason…" />
                </SelectTrigger>
                <SelectContent>
                  {ARCHIVE_REASONS.map(({ value, label, icon: Icon, tone }) => (
                    <SelectItem key={value} value={value} data-testid={`archive-reason-${value.toLowerCase()}`}>
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${tone}`} />
                        {label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Outcome context, debrief notes, lessons learned…"
                value={archiveNotes}
                onChange={(e) => setArchiveNotes(e.target.value)}
                rows={3}
                data-testid="archive-notes-input"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isArchiving}
            data-testid="archive-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleArchive}
            disabled={isArchiving || !archiveReason}
            data-testid="confirm-archive-btn"
          >
            {isArchiving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Archive Pursuit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
